<script lang="ts" setup>
import { computed, onMounted, PropType, ref, watch } from 'vue';
import {
  PopoverColorEnum,
  PopoverPositionEnum,
  TirCheckbox,
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
  TirPmIconView,
  TirPmPopover,
  TirPmScrollbar,
  TirPmTooltipElement,
} from 'tir-components';
import { TirPmMultiselect } from 'tir-pm-multiselect';
import { UserCircleIcon } from 'tir-style-system/icons/outline';
import { UserModel } from '@/components/task-forms/models';
import { EmployeeMultiselectOptionModel } from './models';
import EnumUserStatus from '@/api/Main/dataSource/api/users/base/enums/EnumUserStatus';

// #region defineProps
const props = defineProps({
  /**Статус филтра */
  statusFilter: { type: String },
  /**Идентификаторы выбранных сотрудников */
  modelValue: {
    type: Array as PropType<Array<number>>,
    default: [],
  },
  /**Список сотрудников */
  employees: {
    type: Array as PropType<Array<UserModel>>,
    default: [],
  },
  popoverWidth: { type: String, default: '32rem' },
  /**Позиция поповера */
  popoverPosition: {
    type: Object,
    default: () => ({
      left: '0rem',
      top: '$el:height + 0.25rem',
    }),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});
// #endregion defineProps

// #region defineEmits
const emit = defineEmits<{
  /**Отправка id выбранных пользователей */
  'update:modelValue': [number[]];
  /**Открытие мультиселекта */
  open: [];
}>();
// #endregion defineEmits

// #region refs
/**Строка поиска */
const searchStr = ref('');
/**Компонент мультиселекта */
const $multiselect = ref();
// #endregion refs

// #region computed
/**Опции для мультиселекта */
const options = computed(() =>
  props.employees.map(
    (employee) =>
      new EmployeeMultiselectOptionModel({
        Id: employee.Id,
        FirstName: employee.FirstName,
        LastName: employee.LastName,
        AvatarColor: employee.AvatarColor,
        ImageUrl: employee.ImageUrl,
      }),
  ),
);
/**Не выбранные опции */
const unselectOptions = computed(() => {
  return options.value
    .filter(
      (option) =>
        !props.modelValue.some((employeeId) => option.Id === employeeId) &&
        (!searchStr.value || option.search(searchStr.value)),
    )
    .sort((a, b) => a.FirstAndLastName.localeCompare(b.FirstAndLastName));
});
/**Выбранные опции */
const selectedOptions = computed(() =>
  options.value
    .filter((option) => props.modelValue.some((employeeId) => option.Id === employeeId))
    .sort((a, b) => a.FirstAndLastName.localeCompare(b.FirstAndLastName)),
);

// #endregion computed

// #region Функции
/**Выбрать / убрать выбор сотрудника */
const toggleEmployee = (employeeId: number) => {
  let newValue = [...props.modelValue];
  // Если сотрудник уже был выбран
  if (props.modelValue.includes(employeeId)) {
    newValue = newValue.filter((_employeeId) => employeeId !== _employeeId);
  } else {
    newValue.push(employeeId);
  }

  emit('update:modelValue', newValue);
};
/**Закрыть компонент */
const close = () => {
  $multiselect.value?.close?.();
};
// #endregion Функции

/**Код для использования по ref */
defineExpose({ close });
</script>

<template>
  <div class="employee-multiselect">
    <TirPmMultiselect
      ref="$multiselect"
      :isDisabled="statusFilter === String(EnumUserStatus.Dismissed) || disabled"
      :options="options"
      :popover-width="popoverWidth"
      :disable-content-blur="true"
      :popover-position="popoverPosition"
      @update-search="searchStr = $event"
      @onOpen="emit('open')"
    >
      <template #target>
        <slot name="target">
          <TirPmButton
            :variant="TirPmButtonVariantEnum.LinkState"
            :size="TirPmButtonSizeEnum.Small"
          >
            <template #leftSlot>
              <UserCircleIcon />
            </template>
            <template #default>
              <div class="employee-multiselect__button-content">
                <span>Сотрудник:</span>
                <span v-if="!selectedOptions.length">Все</span>
                <div class="employee-multiselect__button-users" v-else>
                  <div
                    v-for="(option, index) in selectedOptions"
                    :style="{ transform: `translateX(-${0.25 * index}rem)` }"
                  >
                    <TirPmTooltipElement>
                      <TirPmIconView
                        :size="24"
                        :image-load-url="option.ImageUrl"
                        :color-code="option.AvatarColor"
                        is-rounded
                        :is-bordered="false"
                      >
                        <template #mainSlot>
                          {{ option.FirstAndFirstLetterFullname }}
                        </template>
                      </TirPmIconView>
                      <template #content>
                        <div class="employee-multiselect__tooltip-text">
                          {{ option.FirstAndLastName }}
                        </div>
                      </template>
                    </TirPmTooltipElement>
                  </div>
                </div>
              </div>
            </template>
          </TirPmButton>
        </slot>
      </template>
      <template #optionsContent>
        <TirPmScrollbar
          style="max-height: 12rem; min-height: 12rem"
          :show-on-hover="true"
          @click.stop
        >
          <div class="employee-multiselect__selected-options" v-if="selectedOptions.length">
            <label
              class="employee-multiselect__option"
              v-for="option in selectedOptions"
              :key="option.Id"
              @click="toggleEmployee(option.Id)"
            >
              <TirCheckbox :model-value="true" />
              <TirPmIconView
                :size="24"
                :image-load-url="option.ImageUrl"
                :color-code="option.AvatarColor"
                is-rounded
                :is-bordered="false"
              >
                <template #mainSlot>
                  {{ option.FirstAndFirstLetterFullname }}
                </template>
              </TirPmIconView>
              <span class="employee-multiselect__name">{{ option.FirstAndLastName }}</span>
            </label>
          </div>
          <label
            class="employee-multiselect__option"
            v-for="option in unselectOptions"
            :key="option.Id"
            @click="toggleEmployee(option.Id)"
          >
            <TirCheckbox />
            <TirPmIconView
              :size="24"
              :image-load-url="option.ImageUrl"
              :color-code="option.AvatarColor"
              is-rounded
              :is-bordered="false"
            >
              <template #mainSlot>
                {{ option.FirstAndFirstLetterFullname }}
              </template>
            </TirPmIconView>
            <span class="employee-multiselect__name">{{ option.FirstAndLastName }}</span>
          </label>
        </TirPmScrollbar>
        <slot name="optionsContentFooter" />
      </template>
    </TirPmMultiselect>
  </div>
</template>

<style lang="scss" scoped>
.employee-multiselect {
  &__option {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem;
    cursor: pointer;
    font-size: 1rem;
    line-height: 1.5rem;
    font-weight: 400;

    &:hover {
      background-color: var(--neutral-translucent-5);
    }
    &:active {
      background-color: var(--neutral-translucent-10);
    }
  }
  &__button-content {
    display: flex;
    align-items: center;
    gap: 0.375rem;
  }
  &__selected-options {
    border-bottom: solid 0.063rem var(--neutral-30, #bfc0c3);
  }
  &__button-users {
    display: flex;
    align-items: center;
  }
  &__tooltip-text {
    padding: 0.5rem;
    overflow-wrap: break-word;
  }
}
</style>
